import type { CalendarEvent } from "@/types/events.ts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "wouter";
import { useShellUrlWriter } from "@/components/navigation/shellUrl.ts";
import { eventQueries } from "./api.ts";
import { useEventFromCache } from "./eventDetailHooks.ts";

export type EventDeepLink = {
  /**
   * The event a `/calendar?eventId=…` link asked to focus, once resolved.
   * Undefined when there's no such link, or while it's still resolving.
   */
  event: CalendarEvent | undefined;
  /** Called by the consumer once it has taken ownership of `event`. */
  clear: () => void;
};

/**
 * Resolves a `/calendar?eventId=<sourceEntId>` deep link — how a calendar
 * reminder's push notification asks the page to focus an event — into the event
 * itself. The id is the event's source ent id, which is also the grid's
 * `gcalId`, so an event already on screen resolves straight from the events
 * cache and only a cold deep link costs a fetch.
 *
 * The param is dropped as soon as it's read: the target lives in state from
 * there on, so a refresh or a back navigation doesn't re-open a popover the
 * user just dismissed.
 */
/**
 * Resolve an event id to the event, or undefined while it resolves or when
 * there is no id to resolve.
 *
 * Prefers the grid's copy — an event being looked at is normally already loaded
 * — and falls back to the detail endpoint for a cold load or an event outside
 * the fetched range. Shared because both ways an event is named from outside the
 * grid need it: a reminder's `?eventId=` notification link, and the
 * `/calendar/event/:id` address the calendar's own selection lives at.
 */
export const useEventById = (
  eventId: string | undefined,
): { event: CalendarEvent | undefined; isResolving: boolean } => {
  const cachedEvent = useEventFromCache(eventId ?? "");
  const { data: fetchedEvent, isLoading } = useQuery({
    ...eventQueries.detail(eventId ?? ""),
    enabled: !!eventId && !cachedEvent,
  });
  const event = eventId ? (cachedEvent ?? fetchedEvent) : undefined;
  // Reported rather than left for the caller to infer from a missing event: an
  // id that resolves to nothing — deleted, never existed, request failed — is
  // indistinguishable from one still in flight, and a caller that guesses
  // "still loading" waits forever on a URL naming an event it will never show.
  return { event, isResolving: !!eventId && !event && isLoading };
};

export const useEventDeepLink = (): EventDeepLink => {
  const [searchParams] = useSearchParams();
  const writeShellUrl = useShellUrlWriter();
  const linkedId = searchParams.get("eventId");
  const [eventId, setEventId] = useState<string | undefined>(() => linkedId ?? undefined);

  useEffect(() => {
    if (!linkedId) return;
    setEventId(linkedId);
    writeShellUrl({ params: { eventId: null } }, { replace: true });
  }, [linkedId, writeShellUrl]);

  const { event } = useEventById(eventId);

  const clear = useCallback(() => setEventId(undefined), []);

  return { event, clear };
};
